import AuthMethodSwitch from '@components/AuthMethods/AuthMethodSwitch';
import { ActionsFooter } from '@components/Forms';
import ApprovalDialogButton from '@components/Forms/ApprovalDialog/ApprovalDialogButton';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import HelpTooltip from '@components/Tooltips/HelpTooltip';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	Radio,
	RadioGroup,
	Switch,
	Typography
} from '@mui/material';
import { onSubmitForm } from '@utils/forms';
import { stringRequiredWhenFieldEqualsValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React, { useMemo } from 'react';
import { ISocialProviderSettingsWrapper } from 'types/settings';
import { boolean, object } from 'yup';

interface SocialAccountSetUpProps {
	data?: ISocialProviderSettingsWrapper;
	callbackUrl: string;
	callbackDomain: string;
	globalEnabled: boolean;
	onSave: (
		data: ISocialProviderSettingsWrapper
	) => Promise<ISocialProviderSettingsWrapper>;
}

const FORM_KEYS = {
	Account: 'useSelfAccount',
	clientID: 'clientID',
	secret: 'clientSecret',
	redirectURL: 'redirectURL',
	scope: 'scope',
	trustProvidedEmails: 'trustProvidedEmails'
};

const DEFAULT_FORM: ISocialProviderSettingsWrapper = {
	...Object.values(FORM_KEYS).reduce(
		(prev, current) => ({ ...prev, [current]: '' }),
		{}
	),
	[FORM_KEYS.Account]: false
} as ISocialProviderSettingsWrapper;

const SocialAccountSetUp: React.FC<SocialAccountSetUpProps> = ({
	data,
	onSave,
	callbackUrl,
	callbackDomain,
	globalEnabled
}) => {
	const { addToast } = useToast();

	const settings = useMemo<ISocialProviderSettingsWrapper>(
		() => ({
			...DEFAULT_FORM,
			...data,
			callbackDomain
		}),
		[data, callbackDomain]
	);

	const formik = useFormik<ISocialProviderSettingsWrapper>({
		initialValues: settings,
		validationSchema: object({
			[FORM_KEYS.Account]: boolean(),
			[FORM_KEYS.secret]: stringRequiredWhenFieldEqualsValidationScheme(
				FORM_KEYS.Account,
				true
			),
			[FORM_KEYS.clientID]: stringRequiredWhenFieldEqualsValidationScheme(
				FORM_KEYS.Account,
				true
			)
		}),
		onSubmit: onSubmitForm<ISocialProviderSettingsWrapper>(onSave, addToast)
	});

	const descopeMode = !formik.values.useSelfAccount;
	return (
		<Box
			display="flex"
			flexGrow={1}
			flexDirection="column"
			component="form"
			overflow="hidden"
		>
			<Box
				display="flex"
				flexGrow={1}
				flexDirection="column"
				id="oauth-link-container"
				p={3}
			>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12}>
						<FormControl>
							<FormLabel id="use-my-own-account-group">
								{intl.tr('authMethods.social.fields.useMyOwnAccount')}
							</FormLabel>
							<RadioGroup
								row
								aria-labelledby="use-my-own-account-group"
								name={FORM_KEYS.Account}
								value={!!formik.values.useSelfAccount}
							>
								<FormControlLabel
									value="false"
									control={<Radio />}
									onChange={() => {
										formik.setFieldValue(
											FORM_KEYS.Account,
											!formik.values.useSelfAccount
										);
										formik.setFieldValue(FORM_KEYS.clientID, '');
									}}
									label={intl.tr('authMethods.social.fields.account.descope')}
								/>
								<FormControlLabel
									value="true"
									control={<Radio />}
									onChange={() => {
										formik.setFieldValue(
											FORM_KEYS.Account,
											!formik.values.useSelfAccount
										);
										formik.setFieldValue(
											FORM_KEYS.clientID,
											formik.initialValues.clientID,
											false
										);
									}}
									label={intl.tr('authMethods.social.fields.account.myOwn')}
								/>
							</RadioGroup>
						</FormControl>
					</Grid>

					<Grid item xs={9}>
						{!descopeMode && (
							<TextFieldValidation
								id={FORM_KEYS.clientID}
								formik={formik}
								fullWidth
								required={!!formik.values.useSelfAccount}
								disabled={descopeMode}
								label={intl.tr(
									`authMethods.social.fields.${FORM_KEYS.clientID}`
								)}
							/>
						)}
					</Grid>
					{!descopeMode && (
						<Grid item xs={9}>
							<TextFieldValidation
								id={FORM_KEYS.secret}
								formik={formik}
								fullWidth
								required={!!formik.values.useSelfAccount}
								type="password"
								label={intl.tr(`authMethods.social.fields.${FORM_KEYS.secret}`)}
							/>
						</Grid>
					)}
					{!descopeMode && (
						<>
							<Grid item xs={9}>
								<TextFieldValidation
									id="callbackDomain"
									formik={formik}
									fullWidth
									placeholder={intl.tr(
										`authMethods.social.callbackDomain.placeholder`
									)}
									label={intl.tr('authMethods.social.callbackDomain')}
								/>
							</Grid>
							<Grid item xs={1}>
								<HelpTooltip
									title={intl.tr(`authMethods.social.callbackDomain.tooltip`)}
									placement="right"
								/>
							</Grid>
							<Grid item xs={9}>
								<TextFieldValidation
									id="callbackURL"
									formik={formik}
									value={
										formik.values.callbackDomain
											? `https://${formik.values.callbackDomain}/v1/auth/oauth/callback`
											: callbackUrl
									}
									copy
									externalValue
									label={intl.tr('authMethods.social.callbackUrl')}
									disabled
									fullWidth
								/>
							</Grid>
						</>
					)}
					<Grid item xs={9}>
						<TextFieldValidation
							id={FORM_KEYS.redirectURL}
							formik={formik}
							fullWidth
							autoComplete="on"
							label={intl.tr(
								`authMethods.social.fields.${FORM_KEYS.redirectURL}`
							)}
						/>
					</Grid>
					<Grid item xs={1}>
						<HelpTooltip
							title={intl.tr(
								`authMethods.fields.${FORM_KEYS.redirectURL}.tooltip`
							)}
							placement="right"
						/>
					</Grid>
					<Grid item xs={12}>
						<FormControl fullWidth margin="normal" component="fieldset">
							<FormControlLabel
								control={
									<Switch
										data-testid="trust-email-switch"
										id={FORM_KEYS.trustProvidedEmails}
										checked={formik.values.trustProvidedEmails || false}
										onClick={() =>
											formik.setFieldValue(
												FORM_KEYS.trustProvidedEmails,
												!formik.values.trustProvidedEmails
											)
										}
									/>
								}
								label={
									<Box display="flex" flexDirection="row" alignItems="center">
										<Typography sx={{ pr: 1 }}>
											{intl.tr(
												'authMethods.social.fields.trust.provided.emails'
											)}
										</Typography>
										<HelpTooltip
											title={intl.tr(
												'authMethods.social.fields.trust.provided.emails.tooltip'
											)}
											placement="right"
										/>
									</Box>
								}
							/>
						</FormControl>
					</Grid>
					<AuthMethodSwitch formik={formik} disabled={!globalEnabled} />
				</Grid>
			</Box>
			<ActionsFooter>
				<LoadingButton
					color="primary"
					variant="contained"
					loading={formik.isSubmitting}
					disabled={!formik.dirty}
					sx={{ marginRight: 1 }}
					onClick={() => formik.handleSubmit()}
				>
					{intl.tr('button.save')}
				</LoadingButton>
				<ApprovalDialogButton
					buttonText={intl.tr('button.cancel')}
					onApprove={formik.resetForm}
					disabled={!formik.dirty}
					title={intl.tr('discard.changes.dialog.title')}
					question={intl.tr('discard.changes.dialog.question')}
					approveText={intl.tr('discard.changes.dialog.approve')}
				/>
			</ActionsFooter>
		</Box>
	);
};

SocialAccountSetUp.defaultProps = {
	data: undefined
};

export default SocialAccountSetUp;
